import { useState, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { chatService } from '../services/chatService';
import { PendingMessage } from '../types/chat';
import { useAuthStore } from '../stores/auth';

interface UseVoiceRecordingOptions {
  selectedSubject: string | null;
  selectedThreadId: string | null;
  onMessageSent?: (message: any) => void;
  onPendingMessage?: (message: PendingMessage) => void;
}

export const useVoiceRecording = ({
  selectedSubject,
  selectedThreadId,
  onMessageSent,
  onPendingMessage
}: UseVoiceRecordingOptions) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  
  const { user } = useAuthStore();

  const roundToNearestSecond = (duration: number): number => {
    return Math.round(duration * 10) / 10;
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      chunksRef.current = [];
      startTimeRef.current = Date.now();

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = handleRecordingComplete;
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
      handleRecordingError(error as Error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      setIsRecording(false);
    }
  }, [isRecording]);

  const handleRecordingComplete = useCallback(async () => {
    if (chunksRef.current.length === 0) {
      toast.error('No audio data recorded');
      return;
    }

    const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
    const duration = (Date.now() - startTimeRef.current) / 1000;
    
    await handleVoiceMessage(audioBlob, duration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject, selectedThreadId, user, onMessageSent, onPendingMessage]);

  const handleVoiceMessage = useCallback(async (audioBlob: Blob, duration: number) => {
    console.log('Audio blob details:', {
      type: audioBlob.type,
      size: audioBlob.size,
      duration: duration
    });

    if (!audioBlob.size) {
      toast.error('Audio recording is empty');
      return;
    }

    if (!selectedSubject) {
      toast.error('Select a subject before sending voice notes.');
      return;
    }

    const currentSubjectForRequest = selectedSubject;
    const currentThreadIdForRequest = selectedThreadId || `temp-thread-${Date.now()}`;
    
    const tempId = `temp-audio-${Date.now()}`;
    const tempAudioUrl = URL.createObjectURL(audioBlob);
    const localTime = new Date().toISOString();
    const roundedDuration = roundToNearestSecond(duration);

    const ephemeralMessage: PendingMessage = {
      id: tempId,
      content: null, 
      message_type: 'AUDIO',
      role: 'user',
      sender: user?.email || '', 
      subjectId: currentSubjectForRequest,
      thread_id: currentThreadIdForRequest,
      created_at: localTime,
      updated_at: localTime,
      status: 'sending',
      sequence_number: 0,
      tempAudioUrl,
      voice_duration: roundedDuration,
    };

    // Add to pending messages
    onPendingMessage?.(ephemeralMessage);
    setIsUploading(true);

    try {
      // NOTE: /chat/upload/media already creates the AUDIO message in the DB.
      // We do NOT send a second /chat/messages call (that would duplicate and can violate DB constraints).
      const uploadedMessage = await chatService.uploadMedia(
        audioBlob,
        roundedDuration,
        currentThreadIdForRequest,
        currentSubjectForRequest
      );
      console.log('Voice message uploaded successfully:', uploadedMessage);
      onMessageSent?.(uploadedMessage);
      
    } catch (error) {
      console.error('Error sending voice message:', error);
      toast.error('Failed to send voice message');
      handleRecordingError(error as Error);
    } finally {
      setIsUploading(false);
      URL.revokeObjectURL(tempAudioUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject, selectedThreadId, user, onMessageSent, onPendingMessage]);

  const handleRecordingError = useCallback((error: Error) => {
    console.error('Recording error:', error);
    setIsRecording(false);
    setIsUploading(false);
    
    // Clean up resources
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    
    chunksRef.current = [];
  }, []);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      if (!selectedSubject) {
        toast.error('Select a subject before recording.');
        return;
      }
      startRecording();
    }
  }, [isRecording, selectedSubject, startRecording, stopRecording]);

  return {
    isRecording,
    isUploading,
    startRecording,
    stopRecording,
    toggleRecording,
    handleRecordingError
  };
}; 